import React from "react";
import { StyleProp, StyleSheet, View, ViewProps, ViewStyle } from "react-native";
import { kStyleConst } from "src/kStyle";

type tPdStack = {
  gap?: number;
  direction?: "horizontal" | "vertical";
  layout?: number[];
  isReversed?: boolean;
} & ViewProps;

/**
 * Gets only the valid children of a component,
 * and ignores any nullish or falsy child.
 *
 * @param children the children
 */
const getValidChildren = (children: React.ReactNode) => {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return React.Children.toArray(children).filter((child) => React.isValidElement(child)) as React.ReactElement[];
};

const getStackStyles = (
  options: { isHorizontal?: boolean; isReversed?: boolean } = { isHorizontal: true, isReversed: false },
) => {
  let stackStyles: StyleProp<ViewStyle>;

  if (options.isHorizontal) {
    stackStyles = [styles.flexRow];
    if (options.isReversed) stackStyles.push(styles.flexRowReverse);
  } else {
    stackStyles = [styles.flexColumn];
    if (options.isReversed) stackStyles.push(styles.flexColumnReverse);
  }

  return stackStyles;
};

export const PdStack = React.forwardRef<View, React.PropsWithChildren<tPdStack>>((props, ref) => {
  const { gap = 2, direction = "horizontal", layout, isReversed, style, children, ...rest } = props;
  const isHorizontal = direction === "horizontal";

  const validChildren = getValidChildren(children);
  const count = validChildren.length;

  return (
    <View style={[getStackStyles({ isHorizontal, isReversed }), style]} ref={ref} {...rest}>
      {validChildren.map((child, index) => {
        const isLast = index === count - 1;
        return React.cloneElement(child, {
          ...child.props,
          style: [
            child.props.style,
            isHorizontal && layout ? { flex: layout[index] } : {},
            !isLast
              ? isHorizontal
                ? isReversed
                  ? { marginLeft: gap * kStyleConst.kSpacing }
                  : { marginRight: gap * kStyleConst.kSpacing }
                : isReversed
                ? { marginTop: gap * kStyleConst.kSpacing }
                : { marginBottom: gap * kStyleConst.kSpacing }
              : {},
          ],
        });
      })}
    </View>
  );
});

const styles = StyleSheet.create({
  flexColumn: {
    flexDirection: "column",
  },
  flexColumnReverse: {
    flexDirection: "column-reverse",
  },
  flexRow: {
    flexDirection: "row",
  },
  flexRowReverse: {
    flexDirection: "row-reverse",
  },
});
